import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Building2,
  Send,
  Shield,
  Umbrella,
  CloudRain,
  MapPin,
  Home,
  AlertTriangle,
} from "lucide-react";

const SectionESafety = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { loading, error: apiError } = useSelector((state) => state.canteen);

  const surveyId = localStorage.getItem("survey_id") || "defaultSurvey";
  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const [form, setForm] = useState({
    e1: "",
    e2: "",
    e1Remarks: "",
    e2Remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const surveyData = JSON.parse(
          localStorage.getItem("surveyData") || "{}",
        );
        const sectionE = surveyData[surveyId]?.sections?.e;
        if (sectionE) {
          const { timestamp, ...formData } = sectionE;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, [surveyId]);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        const surveyData = JSON.parse(
          localStorage.getItem("surveyData") || "{}",
        );
        if (!surveyData[surveyId]) surveyData[surveyId] = { sections: {} };
        surveyData[surveyId].sections.e = form;
        localStorage.setItem("surveyData", JSON.stringify(surveyData));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form, surveyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      // Clear remarks when option changes to YES
      ...((name === "e1" || name === "e2") &&
        value === t.global.yes && {
          [`${name}Remarks`]: "",
        }),
    }));

    // Clear error when user starts typing/selecting
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Clear remarks error when remarks field is edited
    if (name.endsWith("Remarks") && errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // E1 validation
    if (!form.e1?.trim()) {
      newErrors.e1 = t.global.err_a1;
    }

    if (form.e1 === t.global.no && !form.e1Remarks?.trim()) {
      newErrors.e1Remarks = "Remarks are required when NO is selected";
    }

    // E2 validation
    if (!form.e2?.trim()) {
      newErrors.e2 = t.global.err_a1;
    }

    if (form.e2 === t.global.no && !form.e2Remarks?.trim()) {
      newErrors.e2Remarks = "Remarks are required when NO is selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.closest(".question-container")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Save locally
      const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
      if (!surveyData[surveyId]) surveyData[surveyId] = { sections: {} };
      surveyData[surveyId].sections.e = {
        ...form,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem("surveyData", JSON.stringify(surveyData));

      // Prepare FormData
      const payload = new FormData();
      payload.append("municipality_id", state?.municipality_id || 1);
      payload.append("ward_id", state?.ward_id || 5);
      payload.append("district_id", state?.district_id || 10);
      payload.append(
        "lattitude_longitude",
        state?.lattitude_longitude || "22.5726,88.3639",
      );
      payload.append("program_name", state?.area_name || "demo kiosk");
      payload.append("current_section", "safetyArngmnt");
      payload.append("reporting_date", new Date().toISOString().split("T")[0]);
      payload.append("area_id", state?.area_id || state?.id);
      payload.append("canteen_type_id", state?.canteen_type || 2);

      // Append form data
      payload.append("safety_arngmnt_E1", yesNoToInt(form.e1));
      payload.append("safety_arngmnt_E1_remarks", form.e1Remarks || "");
      payload.append("safety_arngmnt_E2", yesNoToInt(form.e2));
      payload.append("safety_arngmnt_E2_remarks", form.e2Remarks || "");

      // Mark section E as completed
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.e = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );
      // Remove from partialSections if exists
      const partialSections = JSON.parse(
        localStorage.getItem("partialSections") || "{}",
      );
      delete partialSections.e;
      localStorage.setItem("partialSections", JSON.stringify(partialSections));

      // Dispatch Redux action
      const response = await dispatch(createCanteen(payload));

      // Check response
      if (
        response?.data?.data?.is_safety_arngmnt_survey_done == 1 ||
        response?.data?.is_safety_arngmnt_survey_done == 1
      ) {
        // Show success message
        setShowSuccess(true);

        // Auto-navigate after 2 seconds
        setTimeout(() => {
          setShowSuccess(false);
          navigate("/survey/sections", { state });
        }, 2000);
      } else {
        // alert(t.global.alert_server_error);
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error submitting Section E:", err);
      // alert(
      //   err?.response?.data?.message ||
      //     t.global.alert_server_error,
      // );
      setIsSubmitting(false);
    }
  };

  const yesNoOptions = [t.global.yes, t.global.no];

  // Get display name for survey type
  const getSurveyTypeDisplay = (type) => {
    switch (type) {
      case "kiosk":
        return t.global.survey_type_kiosk;
      case "kitchen":
        return t.global.survey_type_kitchen;
      case "kitchencumDining":
        return t.global.survey_type_kitchendining;
      default:
        return type || t.global.survey_type_default;
    }
  };

  // Check if form is complete for button enablement
  const isFormComplete = () => {
    return (
      form.e1 &&
      form.e2 &&
      (form.e1 !== t.global.no || form.e1Remarks) &&
      (form.e2 !== t.global.no || form.e2Remarks)
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section E submitted successfully.
              </p>
              <div className="flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.E.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.E.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay(state?.surveyType)} {t.global.survey_type_default}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">{t.global.error_title}</p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* E1 - Overhead Shed Protection */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Umbrella size={18} className="mr-2" />
                E1. {t.modules.kiosk.sections.E.questions.overheadShed}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kiosk.sections.E.hints.overheadShed}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.e1 === option
                          ? form.e1 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="e1"
                            value={option}
                            checked={form.e1 === option}
                            onChange={handleChange}
                            className="sr-only"
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.e1 === option
                                ? form.e1 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.e1 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.e1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.e1}
                </p>
              )}

              {form.e1 === t.global.no && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1 text-gray-700 flex items-center">
                    <AlertTriangle size={14} className="mr-1" />
                    Remarks (required when NO is selected):
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    name="e1Remarks"
                    value={form.e1Remarks}
                    onChange={handleChange}
                    placeholder="Please specify the issue with overhead shed protection..."
                    className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.e1Remarks ? "border-red-500" : "border-gray-300"
                    }`}
                    rows={3}
                  />
                  {errors.e1Remarks && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.e1Remarks}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Example: No shed available, damaged shed, insufficient coverage, etc.
                  </p>
                </div>
              )}

              {form.e1 === t.global.yes && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium flex items-center">
                    <CheckCircle size={14} className="mr-1" />
                    Overhead shed is available for weather protection.
                  </p>
                </div>
              )}
            </div>

            {/* E2 - Location Safety */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <CloudRain size={18} className="mr-2" />
                E2. {t.modules.kiosk.sections.E.questions.floodFree}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kiosk.sections.E.hints.floodFree}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.e2 === option
                          ? form.e2 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="e2"
                            value={option}
                            checked={form.e2 === option}
                            onChange={handleChange}
                            className="sr-only"
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.e2 === option
                                ? form.e2 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.e2 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.e2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.e2}
                </p>
              )}

              {form.e2 === t.global.no && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1 text-abmer-700 flex items-center">
                    <AlertTriangle size={14} className="mr-1" />
                    Remarks (required when NO is selected):
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    name="e2Remarks"
                    value={form.e2Remarks}
                    onChange={handleChange}
                    placeholder="Please specify the location safety issues..."
                    className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.e2Remarks ? "border-red-500" : "border-gray-300"
                    }`}
                    rows={3}
                  />
                  {errors.e2Remarks && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.e2Remarks}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Example: Water stagnation observed, located in flood-prone area, poor drainage, etc.
                  </p>
                </div>
              )}

              {form.e2 === t.global.yes && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium flex items-center">
                    <CheckCircle size={14} className="mr-1" />
                    Location is safe from water stagnation and flooding.
                  </p>
                </div>
              )}
            </div>

            {/* Progress Indicator */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between text-sm text-gray-700 mb-2">
                <span className="font-medium">{t.global.progress_label}</span>
                <span>{isFormComplete() ? "100% Complete" : t.global.progress_incomplete || "Incomplete"}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (((form.e1 ? 1 : 0) +
                        ((form.e1 === t.global.no ? (form.e1Remarks ? 1 : 0) : 1) ||
                          (form.e1 === t.global.yes ? 1 : 0)) +
                        (form.e2 ? 1 : 0) +
                        ((form.e2 === t.global.no ? (form.e2Remarks ? 1 : 0) : 1) ||
                          (form.e2 === t.global.yes ? 1 : 0))) /
                        4) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !isFormComplete()}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting || !isFormComplete()
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    {t.global.submit_section_e}
                  </>
                )}
              </button>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-blue-600 font-medium">
                  {t.global.mandatoryFields}
                </span>
                <p className="mt-1 text-xs">
                  {t.global.auto_save_note || "All responses are auto-saved locally"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SectionESafety;